import { Island } from "@ui/primitives/Island";
import { ScrollBox } from "@ui/primitives/Scroll";
import { createComponentId, Sheet, type SheetTravelRange } from "@ui/primitives/Sheet";
import { type ReactNode, useCallback, useMemo, useState } from "react";
import { assertDetentLengths, type DetentStop, detentLabel, isExpanded } from "./detents";
import { persistentSheet } from "./persistent-sheet.styles";

export interface PersistentSheetProps {
  /**
   * Intermediate stops in ascending openness. The fully-expanded stop is
   * implicit and always last, so it is named by `expandedLabel` rather than
   * appearing here.
   */
  stops: DetentStop[];
  /** Announced on the grabber at the implicit fully-expanded stop. */
  expandedLabel: string;
  /** Uncontrolled initial stop, 1-based. */
  defaultDetent?: number;
  /** Controlled active stop (1-based) — drives the sheet, e.g. selecting a pin → peek. */
  activeDetent?: number;
  onActiveDetentChange?: (detent: number) => void;
  /**
   * Bottom edge of the track as a CSS length (e.g. the bottom nav height).
   * The track always runs from the viewport top — fully expanded, the sheet
   * sits behind any fixed top bar layered above it — down to this offset,
   * which is where stops are measured from.
   */
  bottomOffset: string;
  /** Sticky content above the scroll body (e.g. an "N results" heading). */
  header?: ReactNode;
  /** Scrollable body content. */
  children: ReactNode;
  className?: string;
  "aria-label": string;
  /** Accessible name for the grabber step control. Defaults to `aria-label`. */
  grabberLabel?: string;
  /**
   * CSS selectors for fixed chrome (top bar, bottom nav) that must stay
   * interactive while the sheet is raised. They become Silk Islands, so they
   * must NOT overlap the sheet's travel area — an Island captures gestures
   * above the sheet. Content that underlaps the sheet (e.g. the map) is
   * instead served by the sheet going non-modal at its lowest stop.
   */
  interactiveOutside?: string[];
}

/**
 * Always-isOpen snap-point sheet, synthesized from Silk's two detent-sheet
 * examples:
 *
 * - Resting at the FIRST (peek) stop it is non-modal: the page behind (e.g. a
 *   map) is fully interactive, and the visible chrome strip — wrapped in
 *   `Sheet.SpecialWrapper`, Silk's mechanism for dragging a backdrop-less
 *   non-modal sheet in Safari — drags the sheet open.
 * - Raised above peek it is modal: drags anywhere on the panel travel it
 *   through its stops, and at full expansion the body scrolls, handing back
 *   into travel at scroll-top. That is Silk's standard modal scroll↔travel
 *   machinery, which the non-modal configuration cannot run in Safari. Fixed
 *   chrome listed in `interactiveOutside` stays tappable via Silk Islands.
 *
 * The sheet cannot be dismissed — its most-closed position is the first stop.
 * The grabber steps stops for keyboard and assistive tech.
 *
 * For a transient blocking surface use `Dialog` instead — see
 * Foundations/Overlays in Storybook.
 */
export function PersistentSheet({
  stops,
  expandedLabel,
  defaultDetent = 1,
  activeDetent,
  onActiveDetentChange,
  bottomOffset,
  header,
  children,
  className,
  "aria-label": ariaLabel,
  grabberLabel,
  interactiveOutside,
}: PersistentSheetProps) {
  const styles = persistentSheet();
  const grabberName = grabberLabel ?? ariaLabel;
  // Per instance, not per module: two sheets sharing one id would register as
  // the same Silk component.
  const [sheetId] = useState(createComponentId);
  const detentLengths = useMemo(() => {
    assertDetentLengths(stops);
    return stops.map((stop) => stop.length);
  }, [stops]);

  const [uncontrolledDetent, setUncontrolledDetent] = useState(defaultDetent);
  const currentDetent = activeDetent ?? uncontrolledDetent;
  const handleActiveDetentChange = useCallback(
    (detent: number) => {
      setUncontrolledDetent(detent);
      onActiveDetentChange?.(detent);
    },
    [onActiveDetentChange],
  );

  // Every gate reads the RESTING stop, from one clock. The travel range is what
  // Silk reports as a gesture starts and ends, so it settles to [n, n] at rest.
  // Deriving the scroll gate from live travel instead would tear down the very
  // scroll gesture driving the handoff, and the sheet would stick at full.
  const [range, setRange] = useState<SheetTravelRange>({ end: defaultDetent, start: defaultDetent });
  const handleTravelRangeChange = useCallback((next: SheetTravelRange) => setRange(next), []);
  const restingAt = range.start === range.end ? range.start : null;
  const restingAtPeek = restingAt === 1;
  const restingExpanded = restingAt !== null && isExpanded(restingAt, stops);

  const bodyScrolls = isExpanded(currentDetent, stops);
  const currentLabel = detentLabel(stops, expandedLabel, currentDetent);

  return (
    <Sheet.Root
      activeDetent={activeDetent}
      className="contents"
      componentId={sheetId}
      defaultActiveDetent={defaultDetent}
      defaultPresented
      onActiveDetentChange={handleActiveDetentChange}
      // Named so its accessible name lands on a dialog rather than a generic
      // element, where name-from-author is not allowed.
      sheetRole="dialog"
    >
      {/* The page around the modal sheet stays interactive through Islands. */}
      {interactiveOutside?.map((selector) => (
        <Island.Root key={selector} contentGetter={selector} forComponent={sheetId} />
      ))}
      <Sheet.Portal>
        <Sheet.View
          aria-describedby={undefined}
          className={styles.view()}
          contentPlacement="bottom"
          detents={detentLengths}
          // Always mounted, never animated in: the sheet is page furniture.
          enteringAnimationSettings={{ skip: true }}
          inertOutside={!restingAtPeek}
          nativeEdgeSwipePrevention={restingExpanded}
          onClickOutside={{ dismiss: false, stopOverlayPropagation: false }}
          onEscapeKeyDown={{ dismiss: false, stopOverlayPropagation: false }}
          // Page furniture must not steal focus on mount (Safari also treats
          // the programmatic focus as :focus-visible and paints the grabber's
          // focus ring on load).
          onPresentAutoFocus={{ focus: false }}
          onTravelRangeChange={handleTravelRangeChange}
          // Explicit height (not `bottom`): Silk sizes the View itself, so a
          // `bottom` inset would be ignored and stops would ride from the
          // viewport bottom — behind any bottom nav — instead of from the
          // track's bottom edge.
          style={{ height: `calc(var(--height-viewport) - ${bottomOffset})`, top: "0px" }}
          swipeDismissal={false}
          swipeOvershoot={false}
        >
          <Sheet.Content className={styles.content({ className })}>
            <Sheet.Title className="sr-only">{ariaLabel}</Sheet.Title>
            {/* The chrome strip is the peek state's entire visible surface;
             *  the SpecialWrapper makes it draggable while non-modal. The
             *  body scroller must stay OUTSIDE the wrapper — nested in it, the
             *  wrapper swallows the scroll-top handoff. */}
            <Sheet.SpecialWrapper.Root className={styles.chromeWrapper()}>
              <Sheet.SpecialWrapper.Content className={styles.chrome()}>
                {/* The Handle button IS the pill (Silk renders Handle children
                 *  visually hidden as its accessible name — a child span would
                 *  be invisible); the zone div is layout only. */}
                <div className={styles.handle()}>
                  <Sheet.Handle
                    action="step"
                    aria-label={currentLabel ? `${grabberName}, ${currentLabel}` : grabberName}
                    className={styles.handlePill()}
                  />
                </div>
                {header != null ? <div className={styles.header()}>{header}</div> : null}
              </Sheet.SpecialWrapper.Content>
            </Sheet.SpecialWrapper.Root>
            {/* At peek the body is translated off-screen but still in the a11y
                tree and tab order, so it is inert there. Only there: `inert`
                also removes the subtree from hit-testing, so applying it at any
                other stop makes a visible list untappable. */}
            <ScrollBox
              className={styles.body()}
              contentClassName="w-full"
              inert={restingAtPeek}
              onScrollStart={{ dismissKeyboard: true }}
              safeArea="layout-viewport"
              scrollGesture={bodyScrolls ? "auto" : false}
              scrollGestureTrap={{ yEnd: true }}
            >
              {children}
            </ScrollBox>
          </Sheet.Content>
        </Sheet.View>
      </Sheet.Portal>
    </Sheet.Root>
  );
}
